import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip } from "chart.js";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { corTextoSobre, formatarNumero, formatarPercentualCurto, temaGrafico } from "./analiseUtils";

ChartJS.register(ArcElement, Tooltip);

/** % escrito dentro de cada fatia grande o bastante. */
const percentualNaFatia = {
    id: "percentualNaFatia",
    afterDatasetsDraw(chart) {
        const { ctx } = chart;
        const dataset = chart.data.datasets[0];
        const total = dataset.data.reduce((a, v) => a + v, 0);
        const meta = chart.getDatasetMeta(0);
        ctx.save();
        ctx.font = "600 11px Roboto, Helvetica, Arial, sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        meta.data.forEach((arco, i) => {
            const fracao = total > 0 ? dataset.data[i] / total : 0;
            if (fracao < 0.07) return;
            const { x, y } = arco.tooltipPosition();
            ctx.fillStyle = corTextoSobre(dataset.backgroundColor[i]);
            ctx.fillText(formatarPercentualCurto(fracao), x, y);
        });
        ctx.restore();
    },
};

/**
 * Distribuição dos atendimentos por pessoa (rosca com total no centro e legenda ao lado).
 * itens: [{ nome, valor }]. Uma fatia por pessoa, com a mesma cor dela no resto da tela (sem "Outros"), da maior para a
 * menor. As pessoas escolhidas no filtro ficam em negrito na legenda.
 */
export default function GraficoDistribuicao({ itens, cores, selecionados = [], rotuloCentro }) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const tema = temaGrafico(darkMode);

    const comValor = itens.filter(i => i.valor > 0);
    const fatias = [...comValor]
        .sort((a, b) => b.valor - a.valor || a.nome.localeCompare(b.nome))
        .map(i => ({ nome: i.nome, valor: i.valor, cor: cores.cor(i.nome) }));
    const total = comValor.reduce((a, i) => a + i.valor, 0);

    const data = {
        labels: fatias.map(f => f.nome),
        datasets: [{
            data: fatias.map(f => f.valor),
            backgroundColor: fatias.map(f => f.cor),
            borderColor: tema.superficie,
            borderWidth: 2,
            hoverOffset: 6,
        }],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "58%",
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    label: (ctx) => `${ctx.label}: ${formatarNumero(ctx.parsed)} (${formatarPercentualCurto(total > 0 ? ctx.parsed / total : 0)})`,
                },
            },
        },
    };

    return (
        <MDBox display="flex" alignItems="center" gap={2} flexWrap="wrap" justifyContent="center">
            <MDBox position="relative" sx={{ width: 190, height: 190, flexShrink: 0 }}>
                <Doughnut data={data} options={options} plugins={[percentualNaFatia]} />
                <MDBox
                    position="absolute"
                    top={0}
                    left={0}
                    width="100%"
                    height="100%"
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    sx={{ pointerEvents: "none" }}
                >
                    <MDTypography variant="h5" fontWeight="bold" color="text" sx={{ lineHeight: 1.1 }}>
                        {formatarNumero(total)}
                    </MDTypography>
                    <MDTypography variant="caption" color="text" sx={{ opacity: 0.8 }}>
                        {rotuloCentro}
                    </MDTypography>
                </MDBox>
            </MDBox>
            <MDBox sx={{ flex: 1, minWidth: 150 }}>
                {fatias.map(f => (
                    <MDBox key={f.nome} display="flex" alignItems="center" justifyContent="space-between" gap={1} py={0.5}>
                        <MDBox display="flex" alignItems="center" gap={1} minWidth={0}>
                            <MDBox sx={{ width: 10, height: 10, minWidth: 10, borderRadius: "50%", background: f.cor, border: `1px solid ${tema.borda}` }} />
                            <MDTypography
                                variant="caption"
                                color="text"
                                fontWeight={selecionados.includes(f.nome) ? "bold" : "regular"}
                                sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                            >
                                {f.nome}
                            </MDTypography>
                        </MDBox>
                        <MDTypography variant="caption" color="text" fontWeight="bold">
                            {formatarPercentualCurto(total > 0 ? f.valor / total : 0)}
                        </MDTypography>
                    </MDBox>
                ))}
            </MDBox>
        </MDBox>
    );
}
